import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manpowerApi, type MpRequest, type MpRequestDecision } from '@/lib/apiClient';
// Direct files, not the `@/components` barrel — see RequestManpowerModal for why.
import Modal from '@/components/Modal';
import Button from '@/components/Button';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import {
  triageTargets, targetLabel, noteRequired, validateTriage, STATUS_LABELS, type TriageTarget,
} from './lib/requestLifecycle';

/**
 * A scheduler decides a manpower request (#303 slice 3).
 *
 * ⚠️ MOUNTED PER OPEN by RequestsView (`{triage && <TriageRequestModal …/>}`),
 * never kept mounted and reset — the lesson of #439's two review rounds, where
 * two different pieces of state survived a close. Everything here is fresh by
 * construction: the chosen move, the note, the mutation and its last error.
 *
 * ⚠️ "Booked" RECORDS that the scheduler has already put the crew on the grid
 * by hand. This modal writes mp_requests and nothing else; the grid is the
 * scheduler's own act, made after the conversation with the PM (operations,
 * 2026-08-31). The wording on the control says so.
 *
 * A 409 means another scheduler moved this request first. The message says to
 * look at the row again rather than retry — the row they were deciding on is
 * not the row that exists now.
 */
export default function TriageRequestModal({
  request, onClose,
}: {
  request: MpRequest;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const targets = triageTargets(request.status);
  const [to, setTo] = useState<TriageTarget | ''>(targets[0] ?? '');
  // Seeded with the existing note so a scheduler refining it does not retype it.
  const [note, setNote] = useState(request.schedulerNote ?? '');
  const [touched, setTouched] = useState(false);

  const decide = useMutation({
    mutationFn: (body: MpRequestDecision) => manpowerApi.decideRequest(request.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manpower', 'requests'] });
      qc.invalidateQueries({ queryKey: ['manpower', 'request-open-count'] });
      onClose();
    },
  });

  const validation = touched && to ? validateTriage(request.status, to, note) : null;
  const err = decide.error as { response?: { status?: number; data?: { message?: string } } } | null;
  const serverError = decide.isError
    ? (err?.response?.status === 409
        ? 'Someone else changed this request a moment ago. Close this and look at the row again.'
        : err?.response?.data?.message ?? 'The decision could not be saved. Try again.')
    : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!to || validateTriage(request.status, to, note)) return;
    decide.mutate({ status: to, schedulerNote: note.trim() || null });
  }

  const job = [request.pmtJobNumber ?? request.jobNumber, request.projectTitle].filter(Boolean).join(' ');
  const dates = request.startDate || request.endDate ? `${fmtDate(request.startDate)} → ${fmtDate(request.endDate)}` : '—';
  const needsNote = !!to && noteRequired(request.status, to);
  const canSave = targets.length > 0 && !!to && !decide.isPending;

  const LABEL: React.CSSProperties = {
    font: 'var(--ew-fw-semibold) 11px/1.3 var(--ew-font)', letterSpacing: '.06em',
    textTransform: 'uppercase', color: 'var(--ew-n-500)',
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={<>Request #{request.id} — <span style={{ fontVariantNumeric: 'tabular-nums' }}>{job}</span></>}
      closeOnBackdropClick={!decide.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={decide.isPending}>Cancel</Button>
          <Button variant="primary" type="submit" form="mpt-form" disabled={!canSave}>
            {decide.isPending ? 'Saving…' : 'Save decision'}
          </Button>
        </>
      }
    >
      {/* What was asked, so the decision is made against the ask, not from memory. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', fontSize: 13,
        padding: '10px 12px', marginBottom: 14, borderRadius: 'var(--ew-r-md)',
        background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-100)',
      }}>
        <span style={LABEL}>Crew</span><span style={{ fontWeight: 600 }}>{request.crewSize ?? '—'}{request.phase ? ` · ${request.phase}` : ''}</span>
        {request.changeNo && (<>
          <span style={LABEL}>For</span>
          <span>
            {request.changeNo}
            {request.requestType === 'pco' && <span style={{ color: 'var(--ew-n-500)' }}> — proposed; not yet an executed CO. Book it as anticipated crew.</span>}
          </span>
        </>)}
        <span style={LABEL}>Dates</span><span>{dates}</span>
        <span style={LABEL}>Raised by</span><span>{request.submittedBy ?? 'Unknown'}{request.pm && request.pm !== request.submittedBy ? ` · PM ${request.pm}` : ''}</span>
        <span style={LABEL}>Why</span><span style={{ whiteSpace: 'pre-wrap' }}>{request.reason ?? '—'}</span>
        {request.staffingNotes && (<><span style={LABEL}>Staffing</span><span style={{ whiteSpace: 'pre-wrap' }}>{request.staffingNotes}</span></>)}
        <span style={LABEL}>Now</span>
        <span>
          {STATUS_LABELS[request.status]}
          {request.decidedBy ? <span style={{ color: 'var(--ew-n-500)' }}> — {request.decidedBy}{request.decidedAt ? `, ${fmtDateTime(request.decidedAt)}` : ''}</span> : null}
        </span>
      </div>

      {targets.length === 0 ? (
        <div role="status" style={{ fontSize: 13, color: 'var(--ew-n-600)' }}>
          This is imported history — it was dealt with outside the Tracker and cannot be re-decided here.
        </div>
      ) : (
        <form id="mpt-form" onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <legend style={LABEL}>Decision</legend>
            {targets.map((t) => (
              <label key={t} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.4 }}>
                <input type="radio" name="mpt-to" value={t} checked={to === t} onChange={() => setTo(t)} style={{ marginTop: 3 }} />
                <span>{targetLabel(request.status, t)}</span>
              </label>
            ))}
            {to === 'booked' && (
              <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginLeft: 24 }}>
                Booking here records what you already entered on the crew schedule. It does not enter it.
              </div>
            )}
          </fieldset>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="mpt-note" style={LABEL}>
              What was agreed{needsNote ? '' : <span style={{ textTransform: 'none', letterSpacing: 0 }}> (optional)</span>}
            </label>
            <textarea
              id="mpt-note" rows={3} maxLength={4000}
              style={{
                font: 'var(--ew-fw-regular) 14px/1.4 var(--ew-font)', padding: '7px 10px',
                border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', resize: 'vertical',
              }}
              placeholder={to === 'declined' ? 'Why not — the PM reads this.' : to === 'booked' ? 'Who, when, anything the PM should know.' : 'Notes from the conversation with the PM.'}
              value={note} onChange={(e) => setNote(e.target.value)}
            />
            <div style={{ fontSize: 12, color: 'var(--ew-n-400)' }}>The PM sees this on the request. It is not posted to the job’s activity feed.</div>
          </div>

          {(validation || serverError) && (
            <div role="alert" style={{ color: 'var(--ew-danger, #b8321c)', fontSize: 13 }}>{validation ?? serverError}</div>
          )}
        </form>
      )}
    </Modal>
  );
}
